import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { useDirtyFlag } from "@/hooks/useUnsavedChanges";
import { diseasesAPI } from "@/lib/diseases-api";
import { queryKeys } from "@/lib/query-keys";
import { DiseaseFormFields } from "./DiseaseFormFields";
import {
  DISEASE_DEFAULT_VALUES,
  diseaseSchema,
  type DiseaseFormValues,
} from "./diseaseFormSchema";
import { toDiseasePayload } from "./diseaseUtils";

interface DiseaseAddViewProps {
  onBack: () => void;
  onCreated: (diseaseId: string) => void;
}

export const DiseaseAddView = ({ onBack, onCreated }: DiseaseAddViewProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<DiseaseFormValues>({
    resolver: zodResolver(diseaseSchema),
    defaultValues: DISEASE_DEFAULT_VALUES,
    mode: "onChange",
  });

  const { dirtyFields, isDirty, isValid, isValidating } = form.formState;

  useDirtyFlag("disease:add", isDirty, "Add Disease");

  const createMutation = useMutation({
    mutationFn: (values: DiseaseFormValues) =>
      diseasesAPI.create(toDiseasePayload(values)),
    onSuccess: async (disease) => {
      form.reset({
        long_name: disease.long_name ?? "",
        short_name: disease.short_name ?? "",
        other_names: disease.other_names ?? "",
        display_name: disease.display_name ?? "",
        icd10_code: disease.icd10_code ?? "",
        widely_prevalent: disease.widely_prevalent,
        family_linked: disease.family_linked,
        is_featured: disease.is_featured,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.diseases.all });
      toast({
        title: "Disease created",
        description: `${disease.display_name || disease.long_name} has been added.`,
      });
      onCreated(disease.id);
    },
    onError: (error: any) => {
      toast({
        title: "Unable to create disease",
        description: error?.message || "Please review the form and try again.",
        variant: "destructive",
      });
    },
  });

  const isSaving = createMutation.isPending;
  const saveDisabled = isSaving || isValidating || !isDirty || !isValid;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <Card className="flex min-h-0 flex-1 flex-col overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <CardTitle className="z-h1 text-foreground">
                  Add Disease
                </CardTitle>
                {isDirty ? <Badge variant="warning">Editing</Badge> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="disease-add-form"
                disabled={saveDisabled}
                className="min-w-28"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Save Disease"
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Form {...form}>
            <form
              id="disease-add-form"
              onSubmit={form.handleSubmit((values) =>
                createMutation.mutate(values)
              )}
            >
              <Card>
                <CardContent className="pt-6 pb-5">
                  <DiseaseFormFields
                    control={form.control}
                    dirtyFields={dirtyFields}
                  />
                </CardContent>
              </Card>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};
